/**
 * Phone and tablet surfaces of the collaboration features (PhoneHome / PhoneEditor / PhoneActivity boards):
 *
 * - BottomSheet: a sheet that slides up from the bottom, full width, with a grab handle, a title and a close button;
 *   focus moves into it and comes back on close (antd Drawer). Rows inside are 44px touch targets (Collab.css).
 * - ResponsivePopover: a popover on desktop, the same content in a BottomSheet on a phone (presence list, inbox).
 * - CollabFilters: a panel's filters as a side column on desktop, behind a "Filters" button and a sheet on a phone.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Drawer, Popover } from 'antd';
import { FunnelSimple } from '@phosphor-icons/react';
import { usePhone } from '../../hooks/useMediaQuery';
import './Collab.css';

export function BottomSheet({ open, onClose, title, children, footer = null, testId = 'bottom-sheet' }) {
  return (
    <Drawer
      classNames={{ body: 'collab-sheet-body', footer: 'collab-sheet-footer' }}
      closable={{ 'aria-label': 'Close' }}
      destroyOnHidden
      footer={footer}
      onClose={onClose}
      open={open}
      placement="bottom"
      rootClassName="collab-sheet"
      size="auto"
      title={title}
      // Above Flow's own modals (Modal.css overlays go up to 9999999 on a phone), since a sheet can open from one.
      zIndex={10000100}
    >
      <div data-testid={testId}>{children}</div>
    </Drawer>
  );
}

BottomSheet.propTypes = {
  children: PropTypes.node,
  footer: PropTypes.node,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool,
  testId: PropTypes.string,
  title: PropTypes.node,
};

/**
 * `children` is the trigger (one element, it gets the click); `content` is what opens. `open`/`onOpenChange` are
 * optional (controlled). `title` heads the sheet; `popoverTitle` the desktop popover (none by default).
 */
export function ResponsivePopover({ content, title, popoverTitle = null, children, open: openProp, onOpenChange, placement = 'bottomRight', testId }) {
  const phone = usePhone();
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = (next) => {
    if (openProp === undefined) { setOpenState(next); }
    onOpenChange?.(next);
  };
  if (!phone) {
    return (
      <Popover content={content} onOpenChange={setOpen} open={open} placement={placement} title={popoverTitle} trigger="click">
        {children}
      </Popover>
    );
  }
  const trigger = React.Children.only(children);
  return (
    <>
      {React.cloneElement(trigger, {
        'aria-expanded': open,
        'aria-haspopup': 'dialog',
        onClick: (event) => { trigger.props.onClick?.(event); setOpen(!open); },
        onKeyDown: (event) => {
          trigger.props.onKeyDown?.(event);
          if ((event.key === 'Enter' || event.key === ' ') && trigger.type !== 'button') { event.preventDefault(); setOpen(true); }
        },
      })}
      <BottomSheet onClose={() => setOpen(false)} open={open} testId={testId} title={title}>
        {content}
      </BottomSheet>
    </>
  );
}

ResponsivePopover.propTypes = {
  children: PropTypes.element.isRequired,
  content: PropTypes.node,
  onOpenChange: PropTypes.func,
  open: PropTypes.bool,
  placement: PropTypes.string,
  popoverTitle: PropTypes.node,
  testId: PropTypes.string,
  title: PropTypes.node,
};

/** `summary`: what the filters are set to, in words ("7 days · 2 people"), shown on the phone's Filters button. */
export function CollabFilters({ children, summary = '', active = 0 }) {
  const phone = usePhone();
  const [open, setOpen] = useState(false);
  if (!phone) {
    return <aside className="collab-filters">{children}</aside>;
  }
  return (
    <div className="collab-filters-bar">
      <button aria-expanded={open} aria-haspopup="dialog" className="collab-btn collab-filters-toggle" data-testid="filters-toggle" onClick={() => setOpen(true)} type="button">
        <FunnelSimple aria-hidden size={16} />
        Filters{active > 0 ? ` · ${active}` : ''}
      </button>
      {summary && <span className="collab-muted collab-small collab-filters-summary">{summary}</span>}
      <BottomSheet footer={<button className="collab-btn is-primary collab-sheet-done" onClick={() => setOpen(false)} type="button">Show results</button>} onClose={() => setOpen(false)} open={open} testId="filters-sheet" title="Filters">
        <div className="collab-filters is-sheet">{children}</div>
      </BottomSheet>
    </div>
  );
}

CollabFilters.propTypes = {
  active: PropTypes.number,
  children: PropTypes.node,
  summary: PropTypes.string,
};
